import { MessageSquareText, FileText, PenTool, Rocket, Wallet, CalendarClock } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Stagger, StaggerItem, Reveal } from "./Reveal";
import { SITE } from "@/lib/site";

const steps = [
  { n: "01", icon: MessageSquareText, title: "Tell Us About Your Business", text: "Share what your business does, who your customers are and what you want your website to achieve." },
  { n: "02", icon: FileText, title: "Receive Your Quote", text: `We review your requirements and contact you with a quote. A $${SITE.deposit} deposit secures your project and gets work started.` },
  { n: "03", icon: PenTool, title: "We Design Your Website", text: `Your website is designed and built around your business — typically completed in ${SITE.timeline}, depending on the size of the project.` },
  { n: "04", icon: Rocket, title: "We Launch Your New Website & Take Care of It", text: "We launch your website, connect your custom domain and continue looking after it through ongoing website care." },
];

export const Process = () => (
  <section id="process" className="section" aria-labelledby="process-title">
    <div className="container-x">
      <SectionHeading id="process-title" eyebrow="Process" title="From first conversation to launch — and beyond." subtitle="We design and build your website, launch it and take care of it as well." align="center" />

      <Stagger className="relative mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-4" stagger={0.12}>
        <div className="pointer-events-none absolute left-[12%] right-[12%] top-[38px] hidden h-px lg:block bg-gradient-to-r from-transparent via-indigo-400/50 to-transparent" aria-hidden="true" />
        {steps.map(({ n, icon: Icon, title, text }) => (
          <StaggerItem key={n} className="relative">
            <div className="flex flex-col items-start lg:items-center lg:text-center" data-testid={`process-step-${n}`}>
              <div className="relative grid h-[76px] w-[76px] place-items-center rounded-full border border-white/10 bg-ink shadow-[0_0_40px_-10px_rgba(99,102,241,0.6)]">
                <div className="absolute inset-[6px] rounded-full bg-gradient-to-br from-indigo-500/30 to-violet-600/20" aria-hidden="true" />
                <Icon className="relative h-6 w-6 text-indigo-100" aria-hidden="true" />
              </div>
              <span className="mt-6 font-mono text-xs text-indigo-300/80">{n}</span>
              <h3 className="mt-2 text-lg sm:text-xl font-semibold text-white">{title}</h3>
              <p className="mt-2.5 max-w-xs text-sm text-slate-400 leading-relaxed">{text}</p>
            </div>
          </StaggerItem>
        ))}
      </Stagger>

      <Reveal className="mt-16 grid gap-4 sm:grid-cols-2 max-w-3xl mx-auto">
        <div className="card-dark flex items-center gap-4 p-5" data-testid="process-deposit-note">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl border border-white/10 bg-indigo-500/15 text-indigo-200"><Wallet className="h-5 w-5" aria-hidden="true" /></span>
          <div>
            <p className="font-semibold text-white">${SITE.deposit} deposit to get started</p>
            <p className="mt-0.5 text-sm text-slate-400">Secures your project once you're happy with your quote.</p>
          </div>
        </div>
        <div className="card-dark flex items-center gap-4 p-5" data-testid="process-timeline-note">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl border border-white/10 bg-violet-500/15 text-violet-200"><CalendarClock className="h-5 w-5" aria-hidden="true" /></span>
          <div>
            <p className="font-semibold text-white">Completed in {SITE.timeline}</p>
            <p className="mt-0.5 text-sm text-slate-400">Depending on the size and requirements of your website.</p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
